import React from 'react';

import SimpleCollapse from '../../agent/components/SimpleCollapse';

import type { SerializedMediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import Tooltip from '../common/ui/Tooltip';

type SerializedMediaRegistryItem = SerializedMediaRegistry['items'][number];

interface MediaRegistryDisplayProps {
    mediaRegistry: SerializedMediaRegistry | null;
}

interface MediaGroupProps {
    title: string;
    description: string;
    items: SerializedMediaRegistryItem[];
}

const MediaGroup: React.FC<MediaGroupProps> = ({ title, description, items }) => {
    if (items.length === 0) {
        return null;
    }

    return (
        <div className="mb-l5-16">
            <h6 className="fw-semibold mb-l5-4">
                {title} ({items.length})
            </h6>
            <small className="text-muted d-block mb-l5-8">{description}</small>
            <div className="d-flex flex-wrap gap-3">
                {items.map((item) => (
                    <MediaItemCard key={item.id} item={item} />
                ))}
            </div>
        </div>
    );
};

const MediaItemCard: React.FC<{ item: SerializedMediaRegistryItem }> = ({ item }) => {
    const { representation, data } = item;

    // Extract thumbnail URL from the asset data
    // The data structure is AssetContainerModel with background.image or background.video
    // For images: use preview_url or thumbnail_url
    // For videos: use poster_url (static image), NOT thumbnail_url/preview_url (which are .mp4 files)
    const imageAsset = data?.background?.image;
    const videoAsset = data?.background?.video;

    const thumbnailUrl =
        imageAsset?.preview_url || imageAsset?.thumbnail_url || videoAsset?.poster_url || '';
    const title = (
        <div>
            {representation.description}
            <br />
            Tags: {representation.tags}
        </div>
    );

    return (
        <Tooltip key={item.id} title={title}>
            <div
                className="media-item-card border rounded p-l5-8 d-flex flex-column"
                style={{ width: '200px', flexShrink: 0, maxHeight: '300px' }}
            >
                <div
                    className="mb-l5-4 bg-light rounded d-flex align-items-center justify-content-center"
                    style={{ height: '120px', minHeight: '120px', overflow: 'hidden' }}
                >
                    {thumbnailUrl ? (
                        <img
                            src={thumbnailUrl}
                            alt={representation.title || 'Media thumbnail'}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                            }}
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                            }}
                        />
                    ) : (
                        <div className="text-center">
                            <i
                                className="material-symbols-outlined text-muted"
                                style={{ fontSize: '48px' }}
                            >
                                {representation.type === 'video' ? 'videocam' : 'image'}
                            </i>
                            <div className="font-l5 font-l5--extra-small text-muted">
                                No thumbnail
                            </div>
                        </div>
                    )}
                </div>
                <div className="font-l5 font-l5--small text-center mt-l5-4">
                    <div className="d-flex align-items-center justify-content-center">
                        <i
                            className="material-symbols-outlined text-muted"
                            style={{ fontSize: '16px' }}
                        >
                            {representation.type === 'video' ? 'videocam' : 'image'}
                        </i>
                        {item.id}
                    </div>
                </div>
            </div>
        </Tooltip>
    );
};

const MediaRegistryDisplay: React.FC<MediaRegistryDisplayProps> = ({ mediaRegistry }) => {
    if (!mediaRegistry || !mediaRegistry.items || mediaRegistry.items.length === 0) {
        return null;
    }

    // Group items by source category
    const sourceMedia = mediaRegistry.items.filter(
        (item) => item.representation.sourceCategory === 'source',
    );
    const uploadedMedia = mediaRegistry.items.filter(
        (item) => item.representation.sourceCategory === 'uploads',
    );
    const stockMedia = mediaRegistry.items.filter(
        (item) => item.representation.sourceCategory === 'stock',
    );

    const totalCount = mediaRegistry.items.length;

    return (
        <div className="media-registry-display mt-l5-16">
            <SimpleCollapse
                title={
                    <div className="d-flex align-items-center">
                        <span className="me-l5-4" style={{ fontSize: '20px' }}>
                            🖼️
                        </span>
                        <span className="fw-semibold">Media Registry ({totalCount} items)</span>
                    </div>
                }
                defaultExpanded={false}
            >
                <div className="p-l5-8">
                    <MediaGroup
                        title="Source media"
                        description="From article/document - likely depicts specific subject"
                        items={sourceMedia}
                    />
                    <MediaGroup
                        title="Uploaded media"
                        description="User uploaded content"
                        items={uploadedMedia}
                    />
                    <MediaGroup
                        title="Stock media"
                        description="Stock photos and videos"
                        items={stockMedia}
                    />
                </div>
            </SimpleCollapse>
        </div>
    );
};

export default MediaRegistryDisplay;
